<template>
    <!-- LOADER -->
    <v-container v-if="loading">
        <loading-spinner/>
    </v-container>
    <v-container v-else :class="{'padding-button': isMobile}">

        <back-button/>

        <!-- BUTTON ASSIGN -->
        <v-row :class="{'mt-12': !isMobile}">
            <v-col cols="4" offset="8" class="text-right">
                <div :class="isMobile ? 'container-bottom-button' : 'container-top-button'">
                    <div class="bottom-row">
                        <v-btn @click="assignRoutine" color="primary" class="black--text bottom-button"
                               block depressed large :loading="loadingAssign" :disabled="disabled">
                            {{ $t('programs.assign_program') }}
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>
        <!-- DESTINY -->
        <v-row>
            <!-- GROUP BY -->
            <v-col cols="12">
                <v-radio-group v-model="currentGroupBy" row hide-details :label="$t('programs.assign_to')">
                    <v-radio
                        v-for="group in groupBy"
                        :label="group.name"
                        :value="group.value"
                        color="accent"
                    ></v-radio>
                </v-radio-group>
            </v-col>
            <!-- STUDENT FILTER -->
            <v-col cols="12" v-if="currentGroupBy === 'students'">
                <kimsa-combobox
                    :label="$tc('student.student', 1)"
                    :placeholder="$t('common.select_placeholder')"
                    outlined dense solo
                    :items="students"
                    @change="setCurrentStudent"
                    :value="currentStudent"
                    placeholder-class
                    item-value="objectID"
                    searchable
                />
            </v-col>
            <!-- GROUP FILTER -->
            <v-col cols="12" v-if="currentGroupBy === 'groups'">
                <kimsa-combobox
                    :label="$t('common.group')"
                    :placeholder="$t('common.select_placeholder')"
                    outlined dense solo
                    :items="groups"
                    @change="setCurrentGroup"
                    :value="currentGroup"
                    placeholder-class
                    item-value="objectID"
                    searchable
                />
            </v-col>
            <!-- TEAM FILTER -->
            <template v-if="currentGroupBy === 'teams'">
                <v-col cols="12" md="6">
                    <kimsa-combobox
                        :label="$t('common.team')"
                        :placeholder="$t('common.select_placeholder')"
                        outlined dense solo
                        :items="teams"
                        @change="setCurrentTeam"
                        :value="currentTeam"
                        placeholder-class
                        item-value="objectID"
                        searchable
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <kimsa-combobox
                        :label="$t('common.group')"
                        :placeholder="$t('common.select_placeholder')"
                        outlined dense solo
                        :items="teamGroups"
                        @change="setCurrentTeamGroup"
                        :readonly="!currentTeam"
                        :value="currentTeamGroup"
                        placeholder-class
                        item-value="objectID"
                        searchable
                    />
                </v-col>
            </template>
        </v-row>
        <!-- DATES -->
        <v-row>
            <v-col cols="12">
                <kimsa-date-picker
                    :label="$t('routines.from')"
                    solo flat
                    dense outlined
                    color="secondary"
                    @change="startDate = $event"
                    :value="startDate"
                    ref="start"
                />
            </v-col>
        </v-row>
        <!-- DAYS SELECTOR -->
        <v-row>
            <v-col cols="12">
                <span class="label">{{ $t('programs.days_to_assign') }}</span>
                <div class="row mx-auto justify-space-between">
                    <v-checkbox v-for="(day,i) in days" :key="i" :label="day.name" v-model="days[i].active"
                                hide-details color="secondary"/>
                </div>
            </v-col>
        </v-row>
        <!-- ALERT -->
        <v-row class="mt-5">
            <v-col cols="12">
                <v-alert type="warning" border="left" outlined dense>
                    {{ $t('programs.alert_replace_routines') }}
                </v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import {mapActions, mapState} from "vuex";
import {Client, UserIndex} from "@/utils/Algolia";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import {db, FieldValue, Timestamp} from "@/utils/Firebase";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getPayloadBaseAssignedRoutine
} from "@/utils/Exercises";
import BackButton from "@/components/common/BackButton";

export default {
    name: "AssignProgram",
    components: {BackButton, LoadingSpinner, KimsaDatePicker, KimsaCombobox},
    data(vm) {
        return {
            groupBy: [
                {name: vm.$tc('student.student', 2), value: 'students'},
                {name: `${vm.$t('common.group')}s`, value: 'groups'},
                {name: `${vm.$t('common.team')}s`, value: 'teams'},
            ],
            currentGroupBy: 'students',
            students: [],
            currentStudent: null,
            currentGroup: null,
            currentTeam: null,
            currentTeamGroup: null,

            startDate: null,

            days: [
                {name: vm.$t('common.short_monday'), value: 1, active: false},
                {name: vm.$t('common.short_tuesday'), value: 2, active: false},
                {name: vm.$t('common.short_wednesday'), value: 3, active: false},
                {name: vm.$t('common.short_thursday'), value: 4, active: false},
                {name: vm.$t('common.short_friday'), value: 5, active: false},
                {name: vm.$t('common.short_saturday'), value: 6, active: false},
                {name: vm.$t('common.short_sunday'), value: 0, active: false},
            ],

            loading: false,
            loadingAssign: false,
            program: null,
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        disabled(vm = this) {
            return !vm.currentSelection || !vm.startDate || vm.days.filter(day => day.active).length === 0
        },
        programId(vm = this) {
            return vm.$route.params.uid || null
        },
        teamGroups(vm = this) {
            return vm.currentTeam?.groups.map(group => {
                return {
                    teamId: vm.currentTeam.id,
                    ...group,
                    id: group.id,
                }
            }) || []
        },
        currentSelection(vm = this) {
            switch (vm.currentGroupBy) {
                case "students":
                    return vm.currentStudent
                case "groups":
                    return vm.currentGroup
                case "teams":
                    return vm.currentTeamGroup
                default:
                    return null
            }
        },
        collectionName(vm = this) {
            return vm.currentGroupBy === 'students' ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        routinesToCopy(vm = this) {
            return vm.program?.assignedRoutines || []
        },
        routineQuantity(vm = this) {
            return vm.routinesToCopy?.length || 0
        },
        isGroup(vm = this) {
            return vm.collectionName === 'groupAssignedRoutines'
        },
    },
    async mounted() {
        const vm = this
        await vm.loadProgram()
        await vm.loadStudents()
        if (vm.groups.length === 0) await vm.bindGroups(vm.currentUser.uid)
        if (vm.teams.length === 0) await vm.bindTeams(vm.currentUser.uid)
        if(vm.$route.query?.studentId) vm.loadCurrentUser()
    },
    methods: {
        ...mapActions(['bindGroups', 'bindTeams']),
        async loadProgram() {
            const vm = this
            vm.loading = true
            let programRef = await db.collection('programs').doc(vm.programId).get()
            vm.program = programRef.data()
            vm.loading = false
        },
        async loadStudents() {
            const vm = this
            let filter = `studentSubscription.coachId: ${vm.currentUser.uid} AND studentSubscription.paused != 1`
            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({hits}) => {
                    vm.students = hits
                })
                .catch(err => {
                    console.log(err);
                });
        },
        setCurrentStudent(student) {
            const vm = this
            if (student?.name) {
                vm.currentStudent = student
            } else if (student) {
                let foundStudent = vm.students?.find(st => st.name.toLowerCase() === student.toLowerCase()) || null
                foundStudent ? vm.currentStudent = foundStudent : vm.currentStudent = null
            } else {
                vm.currentStudent = null
            }
        },
        setCurrentGroup(group) {
            const vm = this
            if (group?.name) {
                vm.currentGroup = group
            } else if (group) {
                let foundGroup = vm.groups?.find(gr => gr.name.toLowerCase() === group.toLowerCase()) || null
                foundGroup ? vm.currentGroup = foundGroup : vm.currentGroup = null
            } else {
                vm.currentGroup = null
            }
        },
        setCurrentTeam(team) {
            const vm = this
            if (team?.name) {
                vm.currentTeam = team
            } else if (team) {
                let foundTeam = vm.teams?.find(tm => tm.name.toLowerCase() === team.toLowerCase()) || null
                foundTeam ? vm.currentTeam = foundTeam : vm.currentTeam = null
            } else {
                vm.currentTeam = null
            }
        },
        setCurrentTeamGroup(teamGroup) {
            const vm = this
            if (teamGroup?.name) {
                vm.currentTeamGroup = teamGroup
            } else if (teamGroup) {
                let foundTeamGroup = vm.teamGroups?.find(tmg => tmg.name.toLowerCase() === teamGroup.toLowerCase()) || null
                foundTeamGroup ? vm.currentTeamGroup = foundTeamGroup : vm.currentTeamGroup = null
            } else {
                vm.currentTeamGroup = null
            }
        },
        loadCurrentUser() {
            const vm = this
            vm.currentStudent = vm.students.find(student => student.objectID === vm.$route.query.studentId)
        },
        daysToCopy() {
            const vm = this
            let dates = []
            let days = [...vm.days].filter(day => day.active && day.value !== 0).sort((a, b) => a.value - b.value)
            if (vm.days[vm.days.length - 1].active) days.push(vm.days[vm.days.length - 1])

            let currentDay = vm.startDate
            let indexDay = 0
            if (vm.$moment(currentDay).day() > days[indexDay].value)
                currentDay = vm.$moment(currentDay).add(1, 'week')

            for (let i = 0; i < vm.routineQuantity; i++) {
                dates.push(vm.$moment(currentDay).day(days[indexDay].value).format('YYYY-MM-DD'))

                if (indexDay === days.length - 1) {
                    indexDay = 0
                    currentDay = vm.$moment(currentDay).add(1, 'week')
                } else indexDay++
            }

            return dates
        },
        async assignRoutine() {
            const vm = this
            vm.loadingAssign = true
            let range = vm.daysToCopy()
            let index = 0

            for (let date of range) {
                let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
                let assignedRoutine = {
                    ...vm.routinesToCopy[index],
                    coachId: vm.currentUser.uid,
                    img: vm.program?.img || '',
                    imgSavePath: vm.program?.imgSavePath || ''
                }

                let routineName = `${vm.currentSelection.name} - ${dateFormatted}`
                let payload = getPayloadBaseAssignedRoutine(routineName, date, assignedRoutine, assignedRoutine.blocks)

                if (vm.currentSelection.objectID) {
                    payload.studentId = vm.currentSelection.objectID
                    await deleteAssignedRoutineStudentInDate(vm.currentSelection.objectID, date)
                } else {
                    payload.groupId = vm.currentSelection.id
                    payload.teamId = vm.currentSelection.teamId || null
                }

                let assignedRoutineCreated = await db.collection(vm.collectionName).add(payload)

                if (vm.isGroup) {
                    for (let student of vm.currentSelection?.students || []) {
                        await deleteAssignedRoutineStudentInDate(student, date)
                    }
                    await addAssignedRoutinesGroup({id: assignedRoutineCreated.id, ...payload, date: date}, vm.currentSelection.students)
                }

                index++
            }

            await db.collection('programs').doc(vm.programId).update({used: FieldValue.increment(index)})

            vm.loadingAssign = false
            vm.$router.back()
            vm.toast(vm.$t('programs.assigned_program'))
        }
    },
    watch: {
        currentGroupBy(val) {
            if (val === 'students') {
                this.currentGroup = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'groups') {
                this.currentStudent = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'teams') {
                this.currentStudent = null
                this.currentGroup = null
            }
        },
    }
}
</script>

<style scoped>

.label {
    font-size: 15px;
    color: #707070;
}

.container-top-button {
    position: fixed;
    top: 80px;
    right: 30px;
    flex-direction: row;
    display: flex;
    align-items: center;
}

.padding-button {
    margin-top: -50px;
    padding-bottom: 50px;
}

</style>